"use client"

import React, { createContext, useContext, useEffect, useState } from "react"

/**
 * v9.2.35 — Theme Provider
 *
 * Three-mode runtime theme switch driven by a [data-theme] attribute
 * on <html>. CSS vars in globals.css resolve per theme; Tailwind
 * tokens in tailwind.config.ts reference those CSS vars; components
 * using na-* classes theme-switch automatically with no rebuild.
 *
 * Persistence: localStorage key 'na-theme'. SSR-safe — first paint is
 * 'default' until the client mounts and reads the stored preference,
 * at which point [data-theme] flips. The 200ms color transition in
 * globals.css smooths the swap so it doesn't flash jarringly.
 *
 * To opt out of theming (e.g. landing page), wrap that subtree with
 * <div data-theme="default"> to pin a specific palette.
 */

export type Theme = "default" | "dark" | "light"

interface ThemeCtx {
  theme: Theme
  setTheme: (t: Theme) => void
}

const ThemeContext = createContext<ThemeCtx>({
  theme: "default",
  setTheme: () => {},
})

const STORAGE_KEY = "na-theme"

function isValidTheme(v: unknown): v is Theme {
  return v === "default" || v === "dark" || v === "light"
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Default to 'default' for SSR. Client effect below reads localStorage
  // and updates if the user previously selected a different theme.
  const [theme, setThemeState] = useState<Theme>("default")

  // First-mount: hydrate from localStorage and apply data-theme
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const initial: Theme = isValidTheme(stored) ? stored : "default"
      setThemeState(initial)
      document.documentElement.setAttribute("data-theme", initial)
    } catch {
      document.documentElement.setAttribute("data-theme", "default")
    }
  }, [])

  // Apply data-theme on every theme change + persist
  const setTheme = (t: Theme) => {
    setThemeState(t)
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, t)
      } catch {
        /* localStorage may be blocked in private mode — non-fatal */
      }
      document.documentElement.setAttribute("data-theme", t)
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeCtx {
  return useContext(ThemeContext)
}
